// Typed view over config/finance.json — the single place the public site's
// money figures live. Import from here, never from the JSON directly, so the
// shape is asserted once and callers get real types.
import raw from "./finance.json";

export interface NetworkCharge {
  kind: "agreed_power_blocks";
  /** Annual value of shedding 1 kW of agreed power, keyed by block id. */
  perKwYear: Record<string, number>;
  /** Blocks peak shaving actually lowers — the default when the visitor
   *  does not know theirs. The rest are near-free per kW and keep headroom. */
  peakBlocks: number[];
}

export interface Region {
  label: string;
  currency: string;
  symbol: string;
  /** List price, EXCLUDING VAT, in this region's currency. */
  hardwarePrice: number;
  /** Standard installation, excluding VAT. */
  installationPrice: number;
  /** Installation is a "from" price — actual quotes vary by site. */
  installationFrom?: boolean;
  arbitrageSpreadPerKwh: number;
  networkCharge: NetworkCharge | null;
  networkChargeNote?: string;
}

export interface Pricing {
  vatIncluded: boolean;
  includeInstallationInPayback: boolean;
}

export interface FinanceModel {
  roundTripEfficiency: number;
  degradationPerKwh: number;
  captureFactor: number;
  usableDepthOfDischarge: number;
  cheapWindowHours: number;
  peakWindowHours: number;
  peakShare: number;
  minAnnualBenefitForPayback: number;
}

export interface Claims {
  symbol: string;
  tariffMaxPerYear: number;
  arbitrageMaxPerMonth: number;
  exampleBatteryKwh: number;
  proof: {
    annualTariffSaving: number;
    weeklyArbitrageProfit: number;
    optimizationEfficiencyPct: number;
  };
}

export const MODEL: FinanceModel = raw.model;
export const CLAIMS: Claims = raw.claims;
export const PRICING: Pricing = raw.pricing;
export const REGIONS: Record<string, Region> = raw.regions as Record<string, Region>;
export const REGION_CODES: string[] = Object.keys(raw.regions);
export const DEFAULT_REGION: string = raw.defaultRegion;

/** Never returns undefined: an unknown code falls back to the default region
 *  so a stale saved selection cannot blank the calculator. */
export function region(code: string): Region {
  return REGIONS[code] ?? REGIONS[DEFAULT_REGION];
}

/** Money in the region's own currency — Switzerland is not priced in euro,
 *  so formatting cannot assume a euro sign anywhere on this page. */
export function money(r: Region, value: number): string {
  return `${r.symbol}${Math.round(value)}`;
}

/** What the payback figure is measured against. Understating the investment
 *  would flatter the payback, so installation is included by default —
 *  flip `pricing.includeInstallationInPayback` to price hardware alone. */
export function upfrontCost(r: Region): number {
  return r.hardwarePrice + (PRICING.includeInstallationInPayback ? r.installationPrice : 0);
}

/** Landing-copy amounts, injected into i18next as default interpolation
 *  variables (see i18n/index.ts). Translations carry `{{tariffMax}}` rather
 *  than a typed-in amount, so a price change is one edit to finance.json
 *  instead of three edits that can silently disagree.
 *
 *  Quoted in the claims' own currency, which is the marketing currency (EUR)
 *  and independent of the calculator's per-region formatting. */
export const FINANCE_VARS: Record<string, string> = {
  tariffMax: `${CLAIMS.symbol}${CLAIMS.tariffMaxPerYear}`,
  arbitrageMax: `${CLAIMS.symbol}${CLAIMS.arbitrageMaxPerMonth}`,
  exampleBattery: String(CLAIMS.exampleBatteryKwh),
  hardwarePrice: money(REGIONS[DEFAULT_REGION], REGIONS[DEFAULT_REGION].hardwarePrice),
  installationPrice: money(REGIONS[DEFAULT_REGION], REGIONS[DEFAULT_REGION].installationPrice),
  proofTariff: `${CLAIMS.symbol}${CLAIMS.proof.annualTariffSaving}`,
  proofArbitrage: `${CLAIMS.symbol}${CLAIMS.proof.weeklyArbitrageProfit}`,
  proofEfficiency: String(CLAIMS.proof.optimizationEfficiencyPct),
};
